const TMDB_API_KEY = '0517665f646b7b75f928cff2c4589f84';
const BASE_URL = 'https://api.themoviedb.org/3';

import { Movie } from '../types';
import { getTMDBLanguage } from '../utils/translations';

declare global {
  interface Window {
    __INITIAL_TRENDING_PROMISE__?: Promise<any>;
    __INITIAL_TRENDING_DATA__?: any;
  }
}

// Simple in-memory cache to prevent duplicate requests (especially in React StrictMode)
const requestCache = new Map<string, Promise<any>>();

export const clearTMDBCache = () => {
  requestCache.clear();
};

const fetchWithCache = async (url: string) => {
  if (requestCache.has(url)) {
    return requestCache.get(url);
  }

  const promise = fetch(url).then(res => {
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    return res.json();
  }).catch(err => {
    requestCache.delete(url); // Remove from cache on error
    throw err;
  });

  requestCache.set(url, promise);
  
  // Optional: clear cache after 5 minutes
  setTimeout(() => requestCache.delete(url), 5 * 60 * 1000);
  
  return promise;
};

const genreMap: Record<number, string> = {
  28: 'Action',
  12: 'Adventure',
  16: 'Animation',
  35: 'Comedy',
  80: 'Crime',
  99: 'Documentary',
  18: 'Drama',
  10751: 'Family',
  14: 'Fantasy',
  36: 'History',
  27: 'Horror',
  10402: 'Music',
  9648: 'Mystery',
  10749: 'Romance',
  878: 'Science Fiction',
  10770: 'TV Movie',
  53: 'Thriller',
  10752: 'War',
  37: 'Western',
  10759: 'Action & Adventure',
  10762: 'Kids',
  10763: 'News',
  10764: 'Reality',
  10765: 'Sci-Fi & Fantasy',
  10766: 'Soap',
  10767: 'Talk',
  10768: 'War & Politics',
};

export const getPosterSrcSet = (url?: string): string | undefined => {
  if (!url || !url.includes('image.tmdb.org/t/p/')) return undefined;
  const pathMatch = url.match(/\/t\/p\/[^/]+(\/.+)$/);
  if (!pathMatch) return undefined;
  const imgPath = pathMatch[1];
  return `https://image.tmdb.org/t/p/w154${imgPath} 154w, https://image.tmdb.org/t/p/w185${imgPath} 185w, https://image.tmdb.org/t/p/w342${imgPath} 342w, https://image.tmdb.org/t/p/w500${imgPath} 500w`;
};

export const getHeroSrcSet = (url?: string): string | undefined => {
  if (!url || !url.includes('image.tmdb.org/t/p/')) return undefined;
  const pathMatch = url.match(/\/t\/p\/[^/]+(\/.+)$/);
  if (!pathMatch) return undefined;
  const imgPath = pathMatch[1];
  return `https://image.tmdb.org/t/p/w500${imgPath} 500w, https://image.tmdb.org/t/p/w780${imgPath} 780w, https://image.tmdb.org/t/p/w1280${imgPath} 1280w`;
};

const mapTMDBToMovie = (item: any, type: 'movie' | 'tv'): Movie => {
  // Extract cast and crew
  const cast = item.credits?.cast?.slice(0, 5).map((c: any) => c.name) || [];
  
  // Directors
  let directors: string[] = [];
  if (type === 'movie') {
    directors = item.credits?.crew?.filter((c: any) => c.job === 'Director').map((c: any) => c.name) || [];
  } else {
    // For series, show creators if available, otherwise search directors
    directors = item.created_by?.map((c: any) => c.name) || item.credits?.crew?.filter((c: any) => c.job === 'Director').map((c: any) => c.name) || [];
  }

  // Trailer key
  const trailerKey = item.videos?.results?.find((v: any) => v.site === 'YouTube' && (v.type === 'Trailer' || v.type === 'Teaser' || v.type === 'Clip'))?.key || undefined;

  // Runtime
  let duration = undefined;
  if (type === 'movie') {
    if (item.runtime) {
      const hrs = Math.floor(item.runtime / 60);
      const mins = item.runtime % 60;
      duration = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    }
  } else {
    if (item.episode_run_time && item.episode_run_time.length > 0) {
      duration = `${item.episode_run_time[0]}m`;
    }
  }

  return {
    id: item.id.toString(),
    title: item.title || item.name,
    description: item.overview,
    thumbnail: item.poster_path ? `https://image.tmdb.org/t/p/w342${item.poster_path}` : '',
    backdrop: item.backdrop_path ? `https://image.tmdb.org/t/p/w780${item.backdrop_path}` : (item.poster_path ? `https://image.tmdb.org/t/p/w780${item.poster_path}` : ''),
    videoUrl: '', // Will be generated in Watch.tsx
    category: item.genre_ids ? item.genre_ids.map((id: number) => genreMap[id]).filter(Boolean).join(', ') : (item.genres ? item.genres.map((g: any) => g.name).join(', ') : 'Unknown'),
    rating: item.vote_average ? parseFloat(item.vote_average.toFixed(1)) : undefined,
    year: item.release_date ? parseInt(item.release_date.split('-')[0]) : (item.first_air_date ? parseInt(item.first_air_date.split('-')[0]) : undefined),
    type,
    tmdbId: item.id.toString(),
    imdbId: item.external_ids?.imdb_id || item.imdb_id,
    quality: '1080p',
    isFeatured: false,
    seasons: item.number_of_seasons,
    episodes: item.number_of_episodes,
    cast: cast.length > 0 ? cast : undefined,
    directors: directors.length > 0 ? directors : undefined,
    trailerKey,
    duration: duration || item.duration
  };
};

export const getInitialTrendingMovies = (): Movie[] => {
  if (typeof window === 'undefined') return [];
  try {
    const lang = getTMDBLanguage();
    if (window.__INITIAL_TRENDING_DATA__?.results) {
      const mapped = window.__INITIAL_TRENDING_DATA__.results.map((item: any) => mapTMDBToMovie(item, 'movie'));
      try {
        localStorage.setItem(`cinestream_trending_cache_v1_${lang}`, JSON.stringify({ ts: Date.now(), items: mapped }));
      } catch {}
      return mapped;
    }
    const raw = localStorage.getItem(`cinestream_trending_cache_v1_${lang}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.items) && parsed.items.length > 0) {
        return parsed.items;
      }
    }
  } catch {}
  return [];
};

export const fetchTrending = async (type: 'movie' | 'tv' = 'movie', page: number = 1): Promise<Movie[]> => {
  try {
    const lang = getTMDBLanguage();
    const url = `${BASE_URL}/trending/${type}/day?api_key=${TMDB_API_KEY}&page=${page}&language=${lang}`;

    let data: any = null;
    if (type === 'movie' && page === 1 && typeof window !== 'undefined' && window.__INITIAL_TRENDING_PROMISE__) {
      const initialPromise = window.__INITIAL_TRENDING_PROMISE__;
      window.__INITIAL_TRENDING_PROMISE__ = undefined;
      data = await initialPromise;
      if (data) {
        requestCache.set(url, Promise.resolve(data));
      }
    }

    if (!data) {
      data = await fetchWithCache(url);
    }

    const mapped = data?.results ? data.results.map((item: any) => mapTMDBToMovie(item, type)) : [];
    if (type === 'movie' && page === 1 && mapped.length > 0 && typeof window !== 'undefined') {
      try {
        localStorage.setItem(`cinestream_trending_cache_v1_${lang}`, JSON.stringify({ ts: Date.now(), items: mapped }));
      } catch {}
    }
    return mapped;
  } catch (error) {
    console.error('Error fetching trending:', error);
    return [];
  }
};

export const fetchPopular = async (type: 'movie' | 'tv' = 'movie', page: number = 1): Promise<Movie[]> => {
  try {
    const data = await fetchWithCache(`${BASE_URL}/${type}/popular?api_key=${TMDB_API_KEY}&page=${page}&language=${getTMDBLanguage()}`);
    return data.results ? data.results.map((item: any) => mapTMDBToMovie(item, type)) : [];
  } catch (error) {
    console.error('Error fetching popular:', error);
    return [];
  }
};

export const searchTMDB = async (query: string, page: number = 1): Promise<Movie[]> => {
  try {
    const data = await fetchWithCache(`${BASE_URL}/search/multi?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&page=${page}&language=${getTMDBLanguage()}`);
    return data.results ? data.results
      .filter((item: any) => item.media_type === 'movie' || item.media_type === 'tv')
      .map((item: any) => mapTMDBToMovie(item, item.media_type)) : [];
  } catch (error) {
    console.error('Error searching TMDB:', error);
    return [];
  }
};

export const fetchTopRated = async (type: 'movie' | 'tv' = 'movie', page: number = 1): Promise<Movie[]> => {
  try {
    const data = await fetchWithCache(`${BASE_URL}/${type}/top_rated?api_key=${TMDB_API_KEY}&page=${page}&language=${getTMDBLanguage()}`);
    return data.results ? data.results.map((item: any) => mapTMDBToMovie(item, type)) : [];
  } catch (error) {
    console.error('Error fetching top rated:', error);
    return [];
  }
};

export const fetchDiscover = async (type: 'movie' | 'tv', params: string, page: number = 1): Promise<Movie[]> => {
  try {
    const data = await fetchWithCache(`${BASE_URL}/discover/${type}?api_key=${TMDB_API_KEY}&${params}&page=${page}&language=${getTMDBLanguage()}`);
    return data.results ? data.results.map((item: any) => mapTMDBToMovie(item, type)) : [];
  } catch (error) {
    console.error('Error fetching discover:', error);
    return [];
  }
};

export const fetchDetails = async (id: string, type: 'movie' | 'tv'): Promise<Movie | null> => {
  try {
    const data = await fetchWithCache(`${BASE_URL}/${type}/${id}?api_key=${TMDB_API_KEY}&append_to_response=external_ids,credits,videos&language=${getTMDBLanguage()}`);
    return mapTMDBToMovie(data, type);
  } catch (error) {
    console.error('Error fetching details:', error);
    return null;
  }
};

export const fetchSeasonDetails = async (tvId: string, seasonNumber: number) => {
  try {
    const data = await fetchWithCache(`${BASE_URL}/tv/${tvId}/season/${seasonNumber}?api_key=${TMDB_API_KEY}&language=${getTMDBLanguage()}`);
    return data;
  } catch (error) {
    console.error('Error fetching season details:', error);
    return null;
  }
};

export const fetchRecommendations = async (id: string, type: 'movie' | 'tv'): Promise<Movie[]> => {
  try {
    const data = await fetchWithCache(`${BASE_URL}/${type}/${id}/recommendations?api_key=${TMDB_API_KEY}&language=${getTMDBLanguage()}`);
    return data.results ? data.results.map((item: any) => mapTMDBToMovie(item, type)) : [];
  } catch (error) {
    console.error('Error fetching recommendations:', error);
    return [];
  }
};
